import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from '@mui/material';
import { ArrowBack as BackIcon, NavigateBefore, NavigateNext, Send } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const MatchAnswer = ({ payload, value, onChange }) => {
  const pl = payload || {};
  const left = Array.isArray(pl.left) ? pl.left : [];
  const right = Array.isArray(pl.right) ? pl.right : [];
  const pairs = value?.pairs || left.map((_, i) => [i, i]);

  const setPair = (rowIdx, rightIdx) => {
    const next = pairs.map((p, j) => (j === rowIdx ? [p[0], parseInt(rightIdx, 10)] : p));
    onChange({ pairs: next });
  };

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {left.map((l, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography sx={{ flex: 1 }}>{l}</Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Matches</InputLabel>
            <Select
              label="Matches"
              value={String((pairs[i] && pairs[i][1]) ?? 0)}
              onChange={(e) => setPair(i, e.target.value)}
            >
              {right.map((r, ri) => (
                <MenuItem key={ri} value={String(ri)}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      ))}
    </Stack>
  );
};

const TrainingExamTake = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionIds, setQuestionIds] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [skipped, setSkipped] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/training/exams/${examId}/session`);
      const data = r.data.data || r.data;
      setSession(data.exam);
      const qs = data.questions || [];
      setQuestions(qs);
      setQuestionIds(data.question_ids || qs.map((q) => q.id));
      setAnswers({});
      setSkipped(new Set());
      setIdx(0);
      setResult(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not start exam');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [examId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const q = questions[idx];

  useEffect(() => {
    if (!q || q.question_type !== 'match') return;
    setAnswers((prev) => {
      if (prev[q.id] != null) return prev;
      const left = q.payload?.left || [];
      const rightLen = (q.payload?.right || []).length;
      const riMax = Math.max(0, rightLen - 1);
      return {
        ...prev,
        [q.id]: { pairs: left.map((_, i) => [i, Math.min(i, riMax)]) }
      };
    });
  }, [q?.id, q?.question_type]);

  const total = questions.length;

  const answeredCount = useMemo(() => {
    let n = 0;
    for (const qq of questions) {
      if (skipped.has(qq.id)) continue;
      const a = answers[qq.id];
      if (a === undefined || a === null) continue;
      if (qq.question_type === 'multiple_choice' && (!Array.isArray(a) || a.length === 0)) continue;
      if (qq.question_type === 'short_text' && String(a).trim() === '') continue;
      if (qq.question_type === 'match' && (!a?.pairs || a.pairs.length === 0)) continue;
      n += 1;
    }
    return n;
  }, [answers, questions, skipped]);

  const skippedCount = skipped.size;

  const setAns = (id, v) => {
    setAnswers((prev) => ({ ...prev, [id]: v }));
    setSkipped((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  };

  const skipCurrent = () => {
    if (!q) return;
    setSkipped((s) => new Set(s).add(q.id));
    if (idx < total - 1) setIdx((i) => i + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const skipped_question_ids = [...skipped];
      const r = await api.post(`/training/exams/${examId}/submit`, {
        question_ids: questionIds,
        answers,
        skipped_question_ids
      });
      const data = r.data.data || r.data;
      setResult(data);
      toast.success('Exam submitted');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestionBody = (qq) => {
    const pl = qq.payload || {};
    const v = answers[qq.id];
    switch (qq.question_type) {
      case 'true_false':
        return (
          <RadioGroup row value={v === true ? 'true' : v === false ? 'false' : ''} onChange={(e) => setAns(qq.id, e.target.value === 'true')}>
            <FormControlLabel value="true" control={<Radio />} label="True" />
            <FormControlLabel value="false" control={<Radio />} label="False" />
          </RadioGroup>
        );
      case 'yes_no':
        return (
          <RadioGroup row value={v || ''} onChange={(e) => setAns(qq.id, e.target.value)}>
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        );
      case 'single_choice':
        return (
          <RadioGroup value={v || ''} onChange={(e) => setAns(qq.id, e.target.value)}>
            {(pl.options || []).map((o) => (
              <FormControlLabel key={o.key} value={o.key} control={<Radio />} label={o.text || o.key} />
            ))}
          </RadioGroup>
        );
      case 'multiple_choice': {
        const arr = Array.isArray(v) ? v : [];
        return (
          <Stack spacing={0.5}>
            {(pl.options || []).map((o) => (
              <FormControlLabel
                key={o.key}
                control={
                  <Checkbox
                    checked={arr.includes(o.key)}
                    onChange={(e) => {
                      const next = e.target.checked ? [...arr, o.key] : arr.filter((x) => x !== o.key);
                      setAns(qq.id, next);
                    }}
                  />
                }
                label={o.text || o.key}
              />
            ))}
          </Stack>
        );
      }
      case 'short_text':
        return (
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Your answer"
            value={v ?? ''}
            onChange={(e) => setAns(qq.id, e.target.value)}
          />
        );
      case 'match':
        return <MatchAnswer payload={pl} value={v} onChange={(nv) => setAns(qq.id, nv)} />;
      default:
        return <Typography color="text.secondary">Unsupported type</Typography>;
    }
  };

  if (loading || !session) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (result) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Paper sx={{ p: 3, background: (t) => alpha(t.palette.success.main, 0.08) }}>
          <Typography variant="h5" gutterBottom>
            Results
          </Typography>
          <Typography variant="h4" color="primary" sx={{ mb: 1 }}>
            {result.score_percent != null ? `${result.score_percent}%` : '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Score {result.score_earned} / {result.max_score}
          </Typography>
          {result.pending_manual_grade && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {result.disclaimer ||
                'Short-answer grading may be pending. Your final score can change after review.'}
            </Alert>
          )}
          {result.disclaimer && !result.pending_manual_grade && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {result.disclaimer}
            </Alert>
          )}
        </Paper>
      </Box>
    );
  }

  const cardColors = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b'];

  return (
    <Box
      sx={{
        minHeight: '70vh',
        p: 2,
        background: (theme) =>
          `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${theme.palette.background.default} 45%)`
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} size="small">
          Leave
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`Questions: ${total}`} size="small" />
          <Chip label={`Answered: ${answeredCount}`} color="primary" variant="outlined" size="small" />
          <Chip label={`Skipped: ${skippedCount}`} color="warning" variant="outlined" size="small" />
          <Chip label={`Current: ${total ? idx + 1 : 0} / ${total}`} size="small" />
        </Stack>
      </Stack>

      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
        {session.name}
      </Typography>
      {session.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {session.description}
        </Typography>
      )}

      {session.time_limit_minutes != null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Time limit: {session.time_limit_minutes} minutes (timer not enforced in this version — manage your time.)
        </Alert>
      )}

      {q && (
        <Card
          sx={{
            mb: 2,
            borderLeft: 6,
            borderColor: cardColors[idx % cardColors.length],
            bgcolor: (t) => alpha(cardColors[idx % cardColors.length], 0.06)
          }}
        >
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Question {idx + 1} of {total} · {q.question_type} · {q.points} pts
              {skipped.has(q.id) ? ' · skipped' : ''}
            </Typography>
            <Typography variant="h6" sx={{ my: 2 }}>
              {q.prompt}
            </Typography>
            {renderQuestionBody(q)}
          </CardContent>
        </Card>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
        <Button startIcon={<NavigateBefore />} disabled={idx <= 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
          Previous
        </Button>
        <Button endIcon={<NavigateNext />} disabled={idx >= total - 1} onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}>
          Next
        </Button>
        <Button color="warning" variant="outlined" onClick={skipCurrent} disabled={!q || skipped.has(q?.id)}>
          Skip for now
        </Button>
        <Button onClick={() => setReviewOpen(true)} disabled={skippedCount === 0}>
          Review skipped ({skippedCount})
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="success" endIcon={<Send />} onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit exam'}
        </Button>
      </Stack>

      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Skipped questions</DialogTitle>
        <DialogContent dividers>
          {[...skipped].length === 0 ? (
            <Typography color="text.secondary">None</Typography>
          ) : (
            <Stack spacing={1}>
              {[...skipped].map((id) => {
                const i = questions.findIndex((x) => x.id === id);
                const qq = questions[i];
                return (
                  <Button key={id} fullWidth variant="outlined" onClick={() => { setIdx(i); setReviewOpen(false); }}>
                    Go to question {i + 1}: {(qq?.prompt || '').slice(0, 60)}
                    {(qq?.prompt || '').length > 60 ? '…' : ''}
                  </Button>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingExamTake;
